import { Box2d, Editor, TLShapeId, TLVideoAsset } from '@tldraw/tldraw'
import { exportAs } from './exportAs'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { getVideo } from '../../helpers'
import { generatePresignedUrls, uploadFile } from 'hooks/queries/designs/useCreateDesignMediaObjects'
import { REACT_APP_CLOUDINARY_CLOUD_NAME } from 'env'
import { VideoShape } from 'components/VisualStudioPlayground/Shapes/VideoShape/Types'

const S3_CLOUDINARY_MAPPINGS = {
  'https://blaze-media-uploads.s3.us-west-1.amazonaws.com/': `https://res.cloudinary.com/${REACT_APP_CLOUDINARY_CLOUD_NAME}/video/upload{transform}/uploads/`,
  'https://blaze-media-uploads-for-dev.s3.us-west-1.amazonaws.com/': `https://res.cloudinary.com/${REACT_APP_CLOUDINARY_CLOUD_NAME}/video/upload{transform}/uploads/`,
}

type VideoFrameLayer = 'overlay' | 'underlay'
type VideoFrameImages = Record<VideoFrameLayer, string>

function getShapesAroundVideo(editor: Editor, video: VideoShape, frameId: TLShapeId) {
  const ids = editor.getSortedChildIdsForParent(frameId)
  const overlayIds = ids.filter((id) => {
    const shape = editor.getShape(id)
    if (!shape) return false
    return shape.index > video.index
  })
  const underlayIds = ids.filter((id) => {
    const shape = editor.getShape(id)
    if (!shape) return false
    return shape.index < video.index
  })

  return { overlayIds, underlayIds: [...underlayIds, frameId] }
}

async function createFrameImage(
  editor: Editor,
  fileId: string,
  frameId: string,
  ids: TLShapeId[],
  bounds: Box2d,
  layer: VideoFrameLayer,
) {
  if (ids.length === 0) return ''

  const frame = await exportAs(editor, ids, 'png', { bounds, background: layer === 'underlay' })
  const timestamp = Math.floor((Date.now() / 1000) | 0).toString()
  const url = `${frameId}_${layer}_${timestamp}`
  const presignedUrl = await generatePresignedUrls(fileId, [url])
  await uploadFile(presignedUrl[0], frame)

  return btoa(presignedUrl[0].split('?')[0])
}

async function exportVideoFrameImages(
  editor: Editor,
  video: VideoShape,
  fileId: string,
  frameId: TLShapeId,
  bounds: Box2d,
  videoSize: 'fit' | 'fill',
): Promise<VideoFrameImages> {
  const { overlayIds, underlayIds } = getShapesAroundVideo(editor, video, frameId)
  const needsUnderlay = underlayIds.length > 0 && videoSize === 'fit'
  const underlayUrl = needsUnderlay
    ? await createFrameImage(editor, fileId, frameId, underlayIds, bounds, 'underlay')
    : ''
  const overlayUrl = await createFrameImage(editor, fileId, frameId, overlayIds, bounds, 'overlay')

  const result: VideoFrameImages = {
    underlay: underlayUrl,
    overlay: overlayUrl,
  }

  return result
}

function generateVideoUrl(
  videoSrc: string,
  video: VideoShape,
  bounds: Box2d,
  presignedUrls: VideoFrameImages,
  videoSize: 'fit' | 'fill',
) {
  const videoSizeParam = videoSize === 'fit' ? 'c_pad' : 'c_fill'
  const { videoX, videoY } =
    videoSize === 'fit' ? { videoX: video.x, videoY: video.y } : { videoX: -video.x, videoY: -video.y }
  const sizeParams = `/w_${bounds.width},h_${bounds.height},${videoSizeParam}`
  const videoParams = `b_transparent,g_north_west,x_${Math.floor(videoX)},y_${Math.floor(videoY)}`
  const underlayParam = presignedUrls.underlay.length ? `/u_fetch:${presignedUrls.underlay}/fl_layer_apply` : ''
  const overlayParam = presignedUrls.overlay.length ? `/l_fetch:${presignedUrls.overlay}/fl_layer_apply` : ''
  let url = videoSrc
  for (const [key, value] of Object.entries(S3_CLOUDINARY_MAPPINGS)) {
    if (url.startsWith(key)) {
      // Replace the placeholder {transform} with the appropriate dimension parameter
      const cloudinaryUrl = value.replace('{transform}', `${sizeParams},${videoParams}${underlayParam}${overlayParam}`)
      url = url.replace(key, cloudinaryUrl)
    }
  }

  return url
}

export async function exportVideoFrame(editor: Editor, fileId: string, frameId: TLShapeId, bounds: Box2d) {
  const video = getVideo(editor, frameId)
  if (!video || !video.props.assetId) {
    throw new Error('Could not find video.')
  }

  const asset = editor.getAsset(video.props.assetId) as TLVideoAsset | undefined
  if (!asset || !asset.props.src || !asset.props.mimeType) {
    throw new Error('Could not find video asset.')
  }

  const frame = editor.getShape<FrameShape>(frameId)
  if (!frame) {
    throw new Error('Could not find frame.')
  }

  const videoSize = video.props.w * video.props.h >= frame.props.w * frame.props.h ? 'fill' : 'fit'

  const presignedUrls = await exportVideoFrameImages(editor, video, fileId, frameId, bounds, videoSize)
  const url = generateVideoUrl(asset.props.src, video, bounds, presignedUrls, videoSize)

  return { url, mimeType: asset.props.mimeType }
}
